import React, { useContext, useEffect, useRef, useState } from 'react';
import { PersonalMessageList } from '../../../types/api/PAL/PersonalMessageList';
import { useWidgetContext } from '../Widget';
import PersonalMessage from './partials/PersonalMessage';
import { PALPanelContext } from '../../../_sections/Personal/PersonalAccessLibrary/PALPanelContext';
import ScrollArea from '../../../components/atoms/ScrollArea/ScrollArea';
import { ContentList, ContentListItem } from '../ContentList';
import { Checkbox, Form, useForm } from '../../Form2';
import { Button } from '../../../components/molecules/Btn/Button';
import { Icons } from '../../../components/atoms/SVG';
import { BtnModifier } from '../../../components/molecules/Btn';
import {
  AcceptDeclineFormTemplate,
} from '../../Form2';
import { useTranslation } from 'react-i18next';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import ContentMessage from '../ContentMessage/ContentMessage';
import { useDelayed } from '../../../hooks/useDelayed';
import usePrevious from '../../../hooks/usePrevious';

export function PersonalMessages({ messageCategory, deleteForm }: PersonalMessageList) {
  const { t } = useTranslation('PersonalMessage');
  const { widgetType } = useWidgetContext();
  const { messagesStates } = useContext(PALPanelContext);
  const { items } = messagesStates?.find(s => s.messageCategory === messageCategory) || { items: [] };

  const [showAcceptDecline, setShowAcceptDecline] = useState(false);
  const [checkedCheckboxes, setCheckedCheckboxes] = useState<string[]>([]);
  const selectAllCheckBoxRef = useRef<HTMLInputElement>(null);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const acceptButtonRef = useRef<HTMLButtonElement>(null);
  const previousShowAcceptDecline = usePrevious(showAcceptDecline);

  useEffect(() => {
    if (!showAcceptDecline && previousShowAcceptDecline) {
      deleteButtonRef.current?.focus();
    } else {
      acceptButtonRef.current?.focus();
    }
  }, [previousShowAcceptDecline, showAcceptDecline]);

  const { formProps, params, fetchState, resetForm, resetFetchState } = useForm(
    {
      form: deleteForm,
      onSuccess: useDelayed(() => {
        setShowAcceptDecline(false);
        setCheckedCheckboxes([]);
        resetForm();
      }) });

  const personalMessageIdParam = params.getByName('personalMessageId');

  const trackCheckedMessages = (checkbox: HTMLInputElement) => {
    if (checkbox.checked) {
      setCheckedCheckboxes([...checkedCheckboxes, checkbox.value]);
    } else {
      setCheckedCheckboxes(checkedCheckboxes.filter(id => id !== checkbox.value));
    }
  };

  return (
    <Form {...formProps} modifier="no-padding">
      <div className="personal-message__top-container">
        {showAcceptDecline
          ? <AcceptDeclineFormTemplate
              messageSuccess={t(`deletableItem.${widgetType}.status.success`)}
              messageSending={t(`deletableItem.${widgetType}.status.sending`)}
              messageFail={t(`deletableItem.${widgetType}.status.fail`)}
              headline={t(`deletableItem.${widgetType}.removeQuestion`)}
              fetchState={fetchState}
              acceptButtonRef={acceptButtonRef}
              onDecline={() => {
                setShowAcceptDecline(false);
                resetFetchState();
              }}
            />
          : <div className="personal-message__check-switch-container">
              <Checkbox
                ref={selectAllCheckBoxRef}
                label={t(`deletableItem.${widgetType}.selectAll`)}
                labelInvisible
                onChange={(event) => {
                  const checkBoxes: NodeListOf<HTMLInputElement> | undefined =
                    formProps.ref.current?.querySelectorAll('[type="checkbox"]');
                  checkBoxes?.forEach((checkBox: HTMLInputElement) => {
                    if (checkBox !== selectAllCheckBoxRef.current) {
                      checkBox.checked = event.target.checked;
                      trackCheckedMessages(checkBox);
                    }
                  });
                }}
                disabled={items.length === 0}
              />
              <Button
                ref={deleteButtonRef}
                type="button"
                title={t(`deletableItem.${widgetType}.remove`)}
                tooltipPlacement={TooltipPlacement.TOP}
                hideTooltipFromScreenReader
                modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
                onClick={() => setShowAcceptDecline(!showAcceptDecline)}
                aria-disabled={checkedCheckboxes.length === 0}
              >
                <Button.Icon svgSpriteId={Icons.DELETE_TRASHCAN} text={t(`deletableItem.${widgetType}.remove`)} />
              </Button>
            </div>}
      </div>
      {items?.length === 0
        ? <ContentMessage text={t(`deletableItem.${widgetType}.noContent`)} />
        : <ScrollArea to={100} bottomOffset={10}>
            <ContentList ordered>
              {items.map((item, index) => {
                return (
                  <ContentListItem key={item.id}>
                    <div className="personal-message__container">
                      <div className="personal-message__checkbox-container">
                        <Checkbox
                          label={t(`deletableItem.${widgetType}.removeLabel`,
                            { position: index + 1, numberOfItems: items.length })}
                          labelInvisible
                          name={personalMessageIdParam.name}
                          value={item.id}
                          onChange={(event) => {
                            if (selectAllCheckBoxRef.current?.checked) {
                              selectAllCheckBoxRef.current.checked = false;
                            }

                            trackCheckedMessages(event.target);
                          }}
                        />
                      </div>
                      <PersonalMessage {...item.content} />
                    </div>
                  </ContentListItem>
                );
              })}
            </ContentList>
          </ScrollArea>}
    </Form>
  );
}
